import { useState, useEffect } from "react";
import { useSearchParams, useNavigate, useLocation } from "react-router-dom";
import "./MyWishlist.css";
import {
  CalendarDays,
  Bookmark,
  Heart,
  ShoppingCart,
  Trash2,
  Zap,
} from "lucide-react";
import Breadcrums from "../../components/breadcrums_v2/Breadcrums";
import ApiService from "../../components/Api_services";
import FallbackImg from "../../components/FallbackImg";
import { useSelector } from "react-redux";
import { ACCESS_KEYS } from "../../constants";
import { hasAccess } from "../../utils/Permission";

function getPagination(current, total) {
  const pages = [];
  if (total <= 7) {
    for (let i = 1; i <= total; i++) pages.push(i);
  } else {
    pages.push(1);
    if (current > 4) pages.push("...");
    let start, end;
    if (current <= 4) {
      start = 2;
      end = 5;
    } else if (current >= total - 3) {
      start = total - 4;
      end = total - 1;
    } else {
      start = current - 1;
      end = current + 1;
    }
    for (let i = start; i <= end; i++) {
      if (i > 1 && i < total) pages.push(i);
    }
    if (current < total - 3) pages.push("...");
    pages.push(total);
  }
  return pages;
}

// Determines label/overlay type for an item (mirrors ProductList logic)
function getFrameType(item) {
  if (!item) return null;
  // Explicit API label takes priority
  if (item.filter_label === "coming_soon") return "coming_soon";
  const launchDate = new Date(item.launching_date);
  const thirtyDaysAgo = new Date();
  thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 30);
  if (
    item.launching_date &&
    item.launching_date !== "0000-00-00 00:00:00" &&
    launchDate > thirtyDaysAgo
  )
    return "new_arrival";
  if (item.promotions && item.promotions.length > 0) return "promo";
  if (
    Number(item.step) > 5 &&
    Number(item.step) < 10 &&
    item.launching_date === "0000-00-00 00:00:00"
  )
    return "coming_soon";
  return null;
}

const formatDate = (dateStr) => {
  if (!dateStr) return "";
  return new Date(dateStr).toLocaleString("en-MY", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  });
};

function formatEtaDate(dateStr) {
  if (!dateStr) return "--";
  return new Date(dateStr).toLocaleString("en-MY", {
    month: "short",
    year: "numeric",
  });
}

const MyWishlist = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const location = useLocation();

  const page =
    parseInt(searchParams.get("page"), 10) > 0
      ? parseInt(searchParams.get("page"), 10)
      : 1;
  const activeTab = searchParams.get("tab") || "wishlist";
  // State variables
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [searchQuery, setSearchQuery] = useState("");
  const [totalPages, setTotalPages] = useState(1);
  const [itemsPerPage] = useState(20);
  const [currentPage, setCurrentPage] = useState(page);
  const [wishlistCount, setWishlistCount] = useState(0);
  const [editMode, setEditMode] = useState(false);
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [totalCount, setTotalCount] = useState(0);

  const sub_role = useSelector((state) => state.auth.sub_role) || [];
  const is_subuser =
    useSelector((state) => state.auth.user?.is_subuser) || false;
  const canOrder = !is_subuser || hasAccess(sub_role, ACCESS_KEYS.MY_ORDER);
  const canView = !is_subuser || hasAccess(sub_role, ACCESS_KEYS.MY_ORDER);

  // ensure defaults exist in URL on mount
  useEffect(() => {
    const params = Object.fromEntries([...searchParams]);
    let changed = false;
    if (!params.tab) {
      params.tab = "wishlist";
      changed = true;
    }
    if (!params.page) {
      params.page = 1;
      changed = true;
    }
    if (changed) {
      setSearchParams(params);
    }
  }, []);

  // keep local currentPage in sync with URL page
  useEffect(() => {
    setCurrentPage(page);
  }, [page]);

  // Fetch items when tab or page changes
  useEffect(() => {
    fetchItems();
  }, [activeTab, page]);

  // Function to fetch items
  const fetchItems = async () => {
    setLoading(true);
    try {
      let response;

      // use URL-derived page (page) instead of local currentPage for API calls
      const pageToFetch = page;

      if (activeTab === "wishlist") {
        response = await ApiService.getMyWishlist(
          pageToFetch,
          itemsPerPage,
          searchQuery,
        );
      } else if (activeTab === "favourite") {
        response = await ApiService.getMyFavourite(
          pageToFetch,
          itemsPerPage,
          searchQuery,
        );
      } else if (activeTab === "buyagain") {
        response = await ApiService.getMyRepeatOrder(
          pageToFetch,
          itemsPerPage,
          searchQuery,
        );
      }

      if (response.status === 200) {
        let fetchedItems = [];
        if (response.data?.items) {
          fetchedItems = response.data.items;
        } else if (Array.isArray(response.data)) {
          fetchedItems = response.data;
        }
        // Deduplicate by wishlist/favorite entry id (cf_id or cw_id), not item id
        // (same product id can appear as both single item and bundle)
        const seen = new Set();
        fetchedItems = fetchedItems.filter((item) => {
          const key = String(
            item.cf_id ??
              item.cw_id ??
              `${item.id}_${item.is_bundle ?? 0}_${item.bundle_id ?? ""}`,
          );
          if (seen.has(key)) return false;
          seen.add(key);
          return true;
        });
        setItems(fetchedItems);
        setTotalCount(
          response.data?.total_count ??
            response.pagination?.total_records ??
            response.pagination?.total_count ??
            fetchedItems.length,
        );
        setTotalPages(response.pagination?.total_pages || 1);
      } else {
        setError(response.message || "Failed to load items");
        setItems([]);
      }
    } catch (err) {
      console.error(`Error loading ${activeTab}:`, err);
      setError(`Failed to load ${activeTab}. Please try again.`);
      setItems([]);
    } finally {
      setLoading(false);
    }
  };

  const isOutOfStock = (item) => {
    if (
      item?.labels?.some((label) =>
        ["out_of_stock", "not_for_sell", "coming_soon"].includes(label.tag),
      )
    ) {
      return true;
    }
    if (String(item?.out_of_stock) === "1") return true;
    if (String(item?.status) === "4") return true;
    if (item?.price_from_rcp) return true;
    
    const stock = Number(item?.stock_quantity ?? item?.stock ?? NaN);
    const brandPrice = Number(
      item?.brand_price ?? item?.pricing?.brand_price ?? NaN,
    );

    const stockIsZeroOrLess = Number.isFinite(stock) ? stock <= 0 : false;
    const brandPriceIsZero = Number.isFinite(brandPrice)
      ? brandPrice === 0
      : false;

    return stockIsZeroOrLess || brandPriceIsZero;
  };

  const isComingSoon = (item) => {
    if (getFrameType(item) === "coming_soon") return true;
    if (item?.filter_label === "coming_soon") return true;
    return Boolean(
      item?.labels?.some((label) => label.tag === "coming_soon"),
    );
  };

  const isDiscontinued = (item) => {
    const stock = Number(item?.stock_quantity ?? item?.stock ?? NaN);
    const brandPrice = Number(
      item?.brand_price ?? item?.pricing?.brand_price ?? NaN,
    );

    const out =
      (Number.isFinite(stock) ? stock <= 0 : false) ||
      (Number.isFinite(brandPrice) ? brandPrice === 0 : false);
    return out && String(item?.status) === "2";
  };

  const handleTabChange = (tab) => {
    setEditMode(false);
    setSelectedIds(new Set());
    setItems([]);
    navigate(`/mywishlist?tab=${tab}&page=1`);
  };

  // Unique key per item for selection tracking
  const getItemKey = (item) => String(item.cw_id ?? item.cf_id ?? item.id);

  const handleSelectItem = (item) => {
    const key = getItemKey(item);
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };

  const handleSelectAll = () => {
    if (selectedIds.size === items.length && items.length > 0) {
      setSelectedIds(new Set());
    } else {
      setSelectedIds(new Set(items.map(getItemKey)));
    }
  };

  const handleBulkRemove = async () => {
    if (selectedIds.size === 0) return;
    setLoading(true);
    try {
      const toRemove = items.filter((item) =>
        selectedIds.has(getItemKey(item)),
      );
      if (activeTab === "wishlist") {
        const wishlistIds = toRemove
          .map((item) => item.cw_id ?? item.wishlist_id)
          .filter(Boolean)
          .join(",");
        await ApiService.removeFromWishlist({ wishlist_id: wishlistIds });
      } else {
        const favoriteIds = toRemove
          .map((item) => item.cf_id ?? item.favorite_id)
          .filter(Boolean)
          .join(",");
        await ApiService.removeFromFavorite({ favorite_id: favoriteIds });
      }
      setSelectedIds(new Set());
      setEditMode(false);
      await fetchItems();
    } catch (err) {
      console.error("Bulk remove error:", err);
      setError("Failed to remove items. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "smooth" });
  }, [currentPage]);

  // Handle remove item
  const handleRemoveItem = async (item) => {
    try {
      setLoading(true);
      const bundle_id = item.bundle_id ?? item.package_id ?? 0;
      const item_id = item.id ?? null;
      let response;

      if (activeTab === "wishlist") {
        const wishlist_id = item.cw_id ?? item.wishlist_id ?? null;
        response = await ApiService.removeFromWishlist({
          wishlist_id,
          item_id,
          bundle_id,
        });
      } else {
        const favorite_id = item.cf_id ?? item.favorite_id ?? null;
        response = await ApiService.removeFromFavorite({
          favorite_id,
          item_id,
          bundle_id,
        });
      }

      if (response?.status === 200) {
        await fetchItems();
      } else {
        setError(response?.message || "Failed to remove item");
      }
    } catch (err) {
      console.error("Error removing item:", err);
      setError("Failed to remove item. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const fetchCounts = async () => {
      try {
        const wishlistResponse = await ApiService.getCountAvailableWishlistItem(
          {},
        );

        if (wishlistResponse?.status === 200) {
          const wishlistTotal =
            wishlistResponse.data.available_wishlist_items || 0;
          setWishlistCount(wishlistTotal);
        }
      } catch (error) {
        console.error("Error fetching counts:", error);
      }
    };

    fetchCounts();
  }, []);

  return (
    <div className="mywishlist_v2">
      <Breadcrums />
      <div className="mywishlist-content_v2">
        {canView ? (
          <>
            {/* ── Header: title + saved count + edit actions ── */}
            <div className="mywishlist-header-container_v2">
              <div className="mywishlist-header_v2">
                <div>
                  <div className="mywishlist-title_v2">
                    {activeTab === "favourite"
                      ? "MY FAVOURITES"
                      : activeTab === "buyagain"
                        ? "BUY AGAIN"
                        : "MY WISHLIST"}
                  </div>
                  <div className="mywishlist-saved-count_v2">
                    {activeTab === "favourite"
                      ? ` ${totalCount} products saved`
                      : activeTab === "buyagain"
                        ? "Top 20 Frequently Purchased Items"
                        : ` ${totalCount} products saved`}
                  </div>
                </div>
                {activeTab !== "buyagain" && canOrder && (
                  <div className="mywishlist-header-actions_v2">
                    {editMode ? (
                      <div className="mywishlist-edit-controls_v2">
                        <button
                          className="mywishlist-selectall-btn_v2"
                          onClick={handleSelectAll}
                        >
                          <input
                            className="mywishlist-checkbox-box_v2"
                            type="checkbox"
                            checked={
                              selectedIds.size > 0 &&
                              selectedIds.size === items.length
                            }
                            onChange={() => {}}
                          />
                          {selectedIds.size > 0 &&
                          selectedIds.size === items.length
                            ? "Deselect All"
                            : "Select All"}
                          {selectedIds.size > 0 && (
                            <span className="mywishlist-selectcount_v2">
                              ({selectedIds.size})
                            </span>
                          )}
                        </button>
                        <button
                          className="mywishlist-cancel-btn_v2"
                          onClick={() => {
                            setEditMode(false);
                            setSelectedIds(new Set());
                          }}
                        >
                          Cancel
                        </button>
                        <button
                          className="mywishlist-remove-btn_v2"
                          onClick={handleBulkRemove}
                          disabled={selectedIds.size === 0}
                          style={{ opacity: selectedIds.size === 0 ? 0.5 : 1 }}
                        >
                          <Trash2 size={15} />
                          Remove
                        </button>
                      </div>
                    ) : (
                      <button
                        className="mywishlist-edit-btn_v2"
                        onClick={() => setEditMode(true)}
                      >
                        Edit
                      </button>
                    )}
                  </div>
                )}
              </div>

              {/* ── Tabs ── */}
              <div className="mywishlist-tabs_v2">
                <div
                  className={`favourite-tab_v2${activeTab === "favourite" ? " active-tab_v2" : ""}`}
                  onClick={() => handleTabChange("favourite")}
                >
                  My Favourite
                </div>
                <div
                  className={`wishlist-tab_v2${activeTab === "wishlist" ? " active-tab_v2" : ""}`}
                  onClick={() => handleTabChange("wishlist")}
                >
                  My Wishlist
                  {wishlistCount > 0 && (
                    <div className="unread-tag_v2">{wishlistCount}</div>
                  )}
                </div>
                <div
                  className={`buyagain-tab_v2${activeTab === "buyagain" ? " active-tab_v2" : ""}`}
                  onClick={() => handleTabChange("buyagain")}
                >
                  Buy Again
                </div>
              </div>
            </div>

            <div className="mywishlist-main_v2">
              {items.length === 0 && loading ? (
                <div className="mywishlist-loading_v2">Loading...</div>
              ) : error ? (
                <div className="mywishlist-error_v2">{error}</div>
              ) : items.length === 0 ? (
                <div className="mywishlist-empty_v2">
                  <div className="mywishlist-empty-icon_v2">
                    {activeTab === "favourite" ? (
                      <Heart size={80} color="#c9d4e8" />
                    ) : activeTab === "buyagain" ? (
                      <ShoppingCart size={80} color="#c9d4e8" />
                    ) : (
                      <Bookmark size={80} color="#c9d4e8" />
                    )}
                  </div>
                  <div className="mywishlist-empty-title_v2">
                    {activeTab === "favourite"
                      ? "No favourites product yet"
                      : activeTab === "buyagain"
                        ? "No previous orders"
                        : "No wishlist product yet"}
                  </div>
                  <div className="mywishlist-empty-subtitle_v2">
                    {activeTab === "favourite"
                      ? "Start adding products to your favourites by clicking the heart icon"
                      : activeTab === "buyagain"
                        ? "Products you've purchased before will appear here for easy reordering"
                        : "Your wishlist is empty. Start adding products you wish to purchase later"}
                  </div>
                  <button
                    className="mywishlist-browse-btn_v2"
                    onClick={() => navigate("/exploreproduct")}
                  >
                    Browse Products
                  </button>
                </div>
              ) : (
                <div className="mywishlist-grid_v2">
                  {items.map((item) => {
                    const itemKey = getItemKey(item);
                    const isSelected = selectedIds.has(itemKey);
                    const showRestockedHeader =
                      !isOutOfStock(item) && !isComingSoon(item);
                    return (
                      <div
                        key={item.cf_id ?? item.cw_id ?? item.id}
                        className={`pl-product_v2 ${isSelected ? "wishlist-selected_v2" : ""} ${showRestockedHeader ? "has-restocked-header_v2" : ""}`}
                        onClick={(e) => {
                          if (editMode) {
                            handleSelectItem(item);
                            return;
                          }
                          if (
                            !e.target.closest(".product-addcart_v2") &&
                            !e.target.closest(".wishlist-fav-btn_v2") &&
                            !e.target.closest("a")
                          ) {
                            const isBundle =
                              String(item.is_bundle) === "1" ||
                              Boolean(item.package_id) ||
                              Boolean(item.bundle_id);
                            const packageId =
                              item.package_id ?? item.bundle_id ?? item.id;
                            const params = new URLSearchParams();
                            params.set("isBundle", isBundle ? "1" : "0");
                            const bundleId =
                              item.bundle_id ?? item.package_id ?? null;
                            if (bundleId) params.set("bundleId", bundleId);
                            navigate(
                              `/productdetail/${item.id}?${params.toString()}`,
                              { state: { isBundle, packageId } },
                            );
                          }
                        }}
                        style={{ cursor: "pointer" }}
                      >
                        {showRestockedHeader && (
                          <div className="wishlist-restocked-header_v2">
                            <Zap size={14} fill="currentColor" />
                            RESTOCKED
                          </div>
                        )}

                        {/* Checkbox overlay in edit mode */}
                        {editMode && (
                          <div
                            className="wishlist-card-checkbox_v2"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleSelectItem(item);
                            }}
                          >
                            <input
                              type="checkbox"
                              checked={isSelected}
                              onChange={() => {}}
                            />
                          </div>
                        )}

                        {/* Top-left New / Promo tags (hidden in edit mode) */}
                        {!editMode &&
                          (() => {
                            const ft = getFrameType(item);
                            const isNew = ft === "new_arrival";
                            const isPromoOnly = ft === "promo";
                            const hasPromoAlso =
                              isNew && item.promotions?.length > 0;
                            if (!isNew && !isPromoOnly) return null;
                            return (
                              <div className="frametag-container_v2">
                                {isNew && (
                                  <span
                                    className="frametag_v2"
                                    style={{ backgroundColor: "#ff6900" }}
                                  >
                                    New
                                  </span>
                                )}
                                {(hasPromoAlso || isPromoOnly) && (
                                  <span
                                    className="frametag_v2"
                                    style={{ backgroundColor: "#ff6900" }}
                                  >
                                    Promo
                                  </span>
                                )}
                              </div>
                            );
                          })()}

                        {/* Top-right bookmark/heart button (hidden in edit mode) */}
                        {!editMode && activeTab !== "buyagain" && (
                          <button
                            className={`${activeTab === "wishlist" ? "pl-bookmark-btn_v2" : "pl-fav-btn_v2"}`}
                            onClick={(e) => {
                              if (!canOrder) return;
                              e.stopPropagation();
                              handleRemoveItem(item);
                            }}
                            title={canOrder ? "Remove item" : "No permission"}
                          >
                            {activeTab === "wishlist" ? (
                              <Bookmark
                                size={18}
                                color="#f47920"
                                fill="#f47920"
                              />
                            ) : (
                              <Heart size={18} color="#e63946" fill="#e63946" />
                            )}
                          </button>
                        )}

                        <div className="product-image-wrapper_v2">
                          <FallbackImg
                            src={item.thumb_url}
                            alt={item.item_name}
                          />

                          {/* Overlay for out-of-stock / coming-soon */}
                          {isOutOfStock(item) &&
                            (() => {
                              const ft = getFrameType(item);
                              const isComingSoon = ft === "coming_soon";
                              const overlayText = isComingSoon
                                ? "COMING SOON"
                                : isDiscontinued(item)
                                  ? "DISCONTINUED"
                                  : "OUT OF STOCK";
                              return (
                                <div className="pl-overlay_v2">
                                  <div className="pl-overlay-text_v2">
                                    {overlayText}
                                  </div>
                                  <div className="pl-eta-pill_v2">
                                    <CalendarDays size={13} />
                                    <span>
                                      {item.eta_date
                                        ? formatDate(item.eta_date)
                                        : "--"}
                                    </span>
                                  </div>
                                </div>
                              );
                            })()}
                        </div>

                        <div className="product-info_v2 standard">
                          <div className="productname_v2">{item.item_code}</div>
                          <div className="description_v2">{item.item_name}</div>
                          {(() => {
                            const raw =
                              item.selling_point ||
                              item.bundle_detail?.selling_point ||
                              "";
                            const parts = raw
                              .split(/\||,/)
                              .map((s) => s.trim())
                              .filter((s) => s);
                            const shown = parts.slice(0, 3);
                            return (
                              <ul className="productcategory_v2">
                                {shown.map((sp, i) => (
                                  <li
                                    key={i}
                                    className="productcategory-item_v2"
                                  >
                                    {sp}
                                  </li>
                                ))}
                              </ul>
                            );
                          })()}

                          {/* Price block */}
                          {!isOutOfStock(item) &&
                            (() => {
                              const brandPrice =
                                item.brand_price != null
                                  ? Number(item.brand_price)
                                  : null;
                              const lowest =
                                item.lowest_price != null
                                  ? Number(item.lowest_price)
                                  : null;
                              const highest =
                                item.highest_price != null
                                  ? Number(item.highest_price)
                                  : null;
                              const showRange =
                                lowest != null &&
                                highest != null &&
                                lowest !== highest;
                              const showLowestAsCurrent =
                                lowest != null &&
                                brandPrice != null &&
                                lowest !== brandPrice;
                              const format = (n) =>
                                Number.isFinite(n) ? n.toFixed(2) : "0.00";
                              return (
                                <div className="price-container_v2">
                                  <div className="price_v2">
                                    <span className="price-sub_v2">FROM</span>{" "}
                                    RM {format(lowest ?? brandPrice)}
                                  </div>
                                </div>
                              );
                            })()}

                          {/* <div className="mywishlist-right_v2">
                          <div className="mywishlist-restock-container_v2">
                            <button
                              type="button"
                              className="product-addcart_v2"
                              onClick={(e) => {
                                if (!canOrder || isOutOfStock(item) || editMode)
                                  return;
                                e.stopPropagation();
                                const isBundle =
                                  String(item.is_bundle) === "1" ||
                                  Boolean(item.package_id) ||
                                  Boolean(item.bundle_id);
                                const packageId =
                                  item.package_id ?? item.bundle_id ?? item.id;
                                const params = new URLSearchParams();
                                params.set("isBundle", isBundle ? "1" : "0");
                                const bundleId =
                                  item.package_id ?? item.bundle_id ?? item.id;
                                if (bundleId) params.set("bundleId", bundleId);
                                navigate(
                                  `/productdetail/${item.id}?${params.toString()}`,
                                  { state: { isBundle, packageId } },
                                );
                              }}
                              disabled={!canOrder || isOutOfStock(item)}
                              style={{
                                visibility: isOutOfStock(item)
                                  ? "hidden"
                                  : "visible",
                                opacity: canOrder ? 1 : 0.5,
                                cursor:
                                  canOrder && !isOutOfStock(item)
                                    ? "pointer"
                                    : "not-allowed",
                              }}
                              title={canOrder ? "" : "No permission"}
                            >
                              <ShoppingCart size={16} />
                              Add to Cart
                            </button>
                          </div>
                        </div> */}
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          </>
        ) : (
          <div
            style={{
              minHeight: "60vh",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <span style={{ color: "#b71c1c", fontWeight: 600 }}>
              You do not have permission to view this section.
            </span>
          </div>
        )}
      </div>
    </div>
  );
};

export default MyWishlist;
